/**
 * Design-time custom-code editing API (current pin of a custom_code_node).
 *
 * Three endpoints on the workflow backend:
 *   GET  .../nodes/{nodeId}/code         — read the current pinned tree
 *   POST .../nodes/{nodeId}/code/commit  — validate + commit text files
 *   POST .../nodes/{nodeId}/code/repin   — new workflow version with new SHA
 *
 * Every save is commit -> repin: a new git commit plus an explicit new
 * workflow version. The backend refuses unsafe paths, oversized or
 * binary content, secret-looking material, broken manifests, stale
 * pins, and concurrently edited directories with typed `{code,message}`
 * errors surfaced here as `NodeCodeError`.
 */

import { apiRequest } from '@/utils/api-request';

export interface EditableSourceFile {
  path: string;
  relative_path: string;
  size_bytes: number;
  content: string;
  language: string;
}

export interface NodeCodeResponse {
  workflow_id: string;
  workflow_version_id: string;
  workflow_version_number: number;
  node_id: string;
  node_display_name: string;
  repo_url: string;
  branch: string;
  script_path: string;
  pinned: boolean;
  pinned_sha: string;
  resolved_sha: string;
  entrypoint: string;
  manifest_name: string;
  manifest_error?: string | null;
  files: EditableSourceFile[];
  can_edit: boolean;
  locked: boolean;
}

export interface CommitNodeCodeResponse {
  commit_sha: string;
  branch: string;
  repo: string;
  script_path: string;
  files_committed: string[];
  base_sha: string;
  previous_pinned_sha: string;
}

export interface RepinNodeResponse {
  workflow_id: string;
  node_id: string;
  script_path: string;
  old_sha: string;
  new_sha: string;
  new_workflow_version_id: string;
  new_workflow_version_number: number;
  software_repo_url: string;
}

export class NodeCodeError extends Error {
  constructor(
    message: string,
    readonly code = 'code_edit_failed',
  ) {
    super(message);
  }
}

interface TypedDetail {
  code?: string;
  message?: string;
}

/**
 * Pull the backend's `{code, message}` detail out of an apiRequest
 * response. 409s surface the object directly on `detail`; other error
 * statuses keep the raw JSON under `data.detail`.
 */
function extractError(response: {
  detail?: unknown;
  data?: unknown;
}): NodeCodeError {
  const candidates: unknown[] = [
    response.detail,
    (response.data as { detail?: unknown } | undefined)?.detail,
  ];
  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object') {
      const typed = candidate as TypedDetail;
      if (typed.message || typed.code) {
        return new NodeCodeError(
          typed.message ?? 'The code operation failed.',
          typed.code ?? 'code_edit_failed',
        );
      }
    }
    if (typeof candidate === 'string' && candidate.trim()) {
      return new NodeCodeError(candidate);
    }
  }
  return new NodeCodeError('The code operation failed.');
}

const base = (orgId: string, workflowId: string, nodeId: string) =>
  `/api/v1/orgs/${encodeURIComponent(orgId)}/workflows/${encodeURIComponent(
    workflowId,
  )}/nodes/${encodeURIComponent(nodeId)}/code`;

export async function getNodeCode(
  orgId: string,
  workflowId: string,
  nodeId: string,
  signal?: AbortSignal,
): Promise<NodeCodeResponse> {
  const response = await apiRequest<NodeCodeResponse>({
    url: base(orgId, workflowId, nodeId),
    target: 'workflow',
    method: 'GET',
    signal,
    suppressSentryStatuses: [400, 404, 409],
  });
  if (response.detail) throw extractError(response);
  return (response.data ?? response) as NodeCodeResponse;
}

export async function commitNodeCode(
  orgId: string,
  workflowId: string,
  nodeId: string,
  body: {
    files: Record<string, string>;
    message?: string;
    script_path?: string;
    expected_pinned_sha?: string;
  },
): Promise<CommitNodeCodeResponse> {
  const response = await apiRequest<CommitNodeCodeResponse>({
    url: `${base(orgId, workflowId, nodeId)}/commit`,
    target: 'workflow',
    method: 'POST',
    body,
    suppressSentryStatuses: [400, 403, 404, 409],
  });
  if (response.detail) throw extractError(response);
  return (response.data ?? response) as CommitNodeCodeResponse;
}

export async function repinNodeCode(
  orgId: string,
  workflowId: string,
  nodeId: string,
  body: {
    new_sha: string;
    expected_old_sha?: string;
    script_path?: string;
  },
): Promise<RepinNodeResponse> {
  const response = await apiRequest<RepinNodeResponse>({
    url: `${base(orgId, workflowId, nodeId)}/repin`,
    target: 'workflow',
    method: 'POST',
    body,
    suppressSentryStatuses: [400, 403, 404, 409],
  });
  if (response.detail) throw extractError(response);
  return (response.data ?? response) as RepinNodeResponse;
}
